/**
 * The one read state. A value exists only on the arms where it is real (`ready`, and
 * `placeholder`, which is the previous key's value kept on screen), so `read.data ?? []`
 * does not compile until the caller has handled the other arms.
 */
export type Read<T> =
  | { readonly status: 'idle'; readonly waitingOn: 'not-requested' | 'parent' }
  | { readonly status: 'loading' }
  | { readonly status: 'offline' }
  | { readonly status: 'failed'; readonly error: Error }
  | { readonly status: 'placeholder'; readonly data: T }
  | { readonly status: 'ready'; readonly data: T; readonly refreshing: Refreshing };

/**
 * What is happening to a value already in hand: a fetch in flight, a fetch the platform paused
 * because it is offline (so it will not land until it reconnects), or nothing.
 */
export type Refreshing = 'fetching' | 'paused' | false;

/** A read that never shows a previous key's value: what a query that did not opt into `keepPrevious` returns. */
export type CurrentRead<T> = Exclude<Read<T>, { status: 'placeholder' }>;

/**
 * A list read. A list is dated rows that each still say what they said, so a failure after rows
 * loaded (usually the next page, sometimes a refetch) keeps them (`partial`) instead of taking the
 * list away. A single figure gets no such arm: a stale figure the reader can't tell is stale
 * misstates the campaign.
 */
export type ListRead<T> =
  | Read<T[]>
  | {
      readonly status: 'partial';
      readonly data: T[];
      /** A failure after rows loaded. */
      readonly error: Error;
      readonly refreshing: Refreshing;
    };

export type AnyRead = { readonly status: string };

/** One handler per arm the read can be in. A missing arm, or one the read can't be in, is a type error. */
export type Arms<Rd extends AnyRead, R> = { [S in Rd['status']]: (arm: Extract<Rd, { status: S }>) => R };

export function match<Rd extends AnyRead, R>(read: Rd, arms: Arms<Rd, R>): R {
  const handle = (arms as unknown as Record<string, (arm: Rd) => R>)[read.status];
  return handle(read);
}

/** The read with its value transformed; every arm without a value passes through unchanged. */
export function mapRead<A, B>(read: Read<A>, f: (data: A) => B): Read<B> {
  switch (read.status) {
    case 'ready':
      return { status: 'ready', data: f(read.data), refreshing: read.refreshing };
    case 'placeholder':
      return { status: 'placeholder', data: f(read.data) };
    default:
      return read;
  }
}

/** The query flags the precedence reads, in the shape TanStack Query returns them. */
export interface QueryFlags {
  isPending: boolean;
  /** The query is paused: TanStack's onlineManager pauses a read while offline. */
  isPaused: boolean;
  isError: boolean;
  error: Error | null;
  isPlaceholderData?: boolean;
  isFetching?: boolean;
}

export interface ReadContext {
  /** The platform's own online signal, for a platform whose onlineManager does not pause reads. */
  online?: boolean;
}

function errorOf(query: QueryFlags): Error {
  return query.error ?? new Error('The read failed');
}

function isOffline(query: QueryFlags, context: ReadContext): boolean {
  return query.isPaused || context.online === false;
}

function refreshingOf(query: QueryFlags, context: ReadContext): Refreshing {
  if (isOffline(query, context)) return 'paused';
  return query.isFetching ? 'fetching' : false;
}

/**
 * The one precedence, for web and mobile alike. Its order is what stops an unanswered read
 * from rendering as a settled one ("No filing yet" to a campaign that has filed):
 *
 * 1. Offline with nothing cached wins, because a paused read never resolves.
 * 2. A failure outranks a cached value.
 * 3. A previous key's value is `placeholder`, never `ready`.
 * 4. Nothing in hand, or a value the query still calls pending, is `loading`. (`idle` is
 *    `defineQuery`'s, for a skipped or waiting query that never asks.)
 *
 * `data` is passed separately because it is not always `query.data`: an infinite query's value
 * is its flattened pages.
 */
export function toRead<T>(query: QueryFlags, data: T | undefined, context: ReadContext = {}): Read<T> {
  const hasData = data !== undefined;
  if (isOffline(query, context) && !hasData) return { status: 'offline' };
  if (query.isError) return { status: 'failed', error: errorOf(query) };
  if (!hasData || query.isPending) return { status: 'loading' };
  if (query.isPlaceholderData) return { status: 'placeholder', data };
  return { status: 'ready', data, refreshing: refreshingOf(query, context) };
}

/** {@link toRead} for a list, where a failure with rows in hand is `partial` (see {@link ListRead}). */
export function toListRead<T>(query: QueryFlags, data: T[] | undefined, context: ReadContext = {}): ListRead<T> {
  if (query.isError && data !== undefined && !query.isPlaceholderData) {
    return { status: 'partial', data, error: errorOf(query), refreshing: refreshingOf(query, context) };
  }
  return toRead(query, data, context);
}
